import * as React from 'react';
import { connect } from 'react-redux';
import { isMoveMediaUploadTokenToPortalStateEnabled } from 'feature-flags';
import { saveAttachment } from 'rest/attachments';
import type { MediaApiUploadToken as PortalMediaApiUploadToken } from 'rest/portal';
import { getUploadCredentials } from 'rest/portal-media';
import type { State } from 'state';
import { fetchPortalUploadMediaTokenAction } from 'state/actions/portal';
import type { MediaApiUploadToken } from 'state/persisted/request-create';
import {
    getMediaApiUploadToken as getPortalMediaApiUploadToken,
    getMediaApiError as getPortalMediaApiError,
} from 'state/selectors/portal';
import { getMediaApiUploadToken, getMediaApiError } from 'state/selectors/request-create';
import type { Auth } from '@atlaskit/media-core';
import type { FieldFormProps, AttachmentFile } from '@atlassian/help-center-common-component/fields/types';
import { RequestCreateAttachmentFieldDumb } from './request-create-attachment-field-dumb';
import { RequestCreateAttachmentFieldError } from './request-create-attachment-field-error';

export interface Props extends FieldFormProps<AttachmentFile[]> {
    portalId: number;
    requestTypeId: number;
}

export const MEDIA_TOKEN_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface StateProps {
    mediaApiUploadToken: MediaApiUploadToken | PortalMediaApiUploadToken | undefined;
    mediaApiError: string | undefined;
}

interface DispatchProps {
    fetchPortalUploadMediaToken: typeof fetchPortalUploadMediaTokenAction;
}

class RequestCreateAttachmentField extends React.PureComponent<Props & StateProps & DispatchProps> {
    private timeout: NodeJS.Timeout | undefined = undefined;
    private mediaUploadAuth: Auth | undefined = undefined;

    authProvider = async (): Promise<Auth> => {
        if (this.mediaUploadAuth) {
            return this.mediaUploadAuth;
        }

        const { portalId } = this.props;
        const { clientId, token, endpointUrl: baseUrl } = await getUploadCredentials(portalId);

        this.mediaUploadAuth = { clientId, token, baseUrl };
        // Ideally token should be expired based on expiryEpoch on the response from getUploadCredentials
        // The token expires in 20 minutes
        this.timeout = setTimeout(() => {
            this.mediaUploadAuth = undefined;
        }, MEDIA_TOKEN_CACHE_TTL_MS); // Cache token for 10 minutes

        return { clientId, token, baseUrl };
    };

    saveAttachment = (fileId: string) => {
        return saveAttachment(this.props.portalId, fileId);
    };

    componentDidMount() {
        if (!this.props.mediaApiUploadToken && isMoveMediaUploadTokenToPortalStateEnabled()) {
            this.props.fetchPortalUploadMediaToken(this.props.portalId);
        }
    }

    componentWillUnmount() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    render() {
        const {
            id,
            label,
            description,
            errorMessage,
            mediaApiError,
            required,
            mediaApiUploadToken,
            blockFormSubmission,
        } = this.props;
        if (mediaApiUploadToken) {
            return (
                <RequestCreateAttachmentFieldDumb
                    {...this.props}
                    mediaApiUploadToken={mediaApiUploadToken}
                    saveAttachment={this.saveAttachment}
                    authProvider={this.authProvider}
                    blockFormSubmission={blockFormSubmission}
                />
            );
        }
        return (
            <RequestCreateAttachmentFieldError
                id={id}
                label={label}
                description={description}
                required={required}
                errorMessage={errorMessage}
                mediaApiError={mediaApiError}
            />
        );
    }
}

const selector = (state: State, props: Props) => {
    const { portalId, requestTypeId } = props;
    const mediaApiUploadToken = isMoveMediaUploadTokenToPortalStateEnabled()
        ? getPortalMediaApiUploadToken(state, portalId)
        : getMediaApiUploadToken(state, portalId, requestTypeId);
    const mediaApiError = isMoveMediaUploadTokenToPortalStateEnabled()
        ? getPortalMediaApiError(state, portalId)
        : getMediaApiError(state, portalId, requestTypeId);
    return { ...props, mediaApiUploadToken, mediaApiError };
};
export default connect(selector, { fetchPortalUploadMediaToken: fetchPortalUploadMediaTokenAction })(
    RequestCreateAttachmentField
);
